import { MRT_ColumnDef } from 'material-react-table';
import { Chip, Stack } from '@mui/material';
import { removeUnderscore } from 'utils/string-utilis';
import { useFilterSelectOptions, ORDER_STATUS_COLOR } from './status';
import { PAYMENT_METHOD, PAYMENT_METHOD_COLOR } from './method';
import { Euro as CashIcon, CreditCard as CardIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ChipCell } from 'components/controls/image-cell';
import { useMemo } from 'react';
import { formatDateWithTime } from 'utils/date-helpers';

export type OrdersColumns = {
    id: string;
    endUserFullName: string;
    placeName: string;
    totalPrice: string;
    orderStatus: string;
    deliveryMethod: string;
    paymentMethod: string;
    receivedNotification: string;
    deliveryTime: string;
    createdAt: string;
    updatedAt: string;
    timeZone?: string;
};

export const useOrdersColumns = (timeZone?: string): MRT_ColumnDef<OrdersColumns>[] => {
    const { t } = useTranslation();
    const filterSelectOptions = useFilterSelectOptions();

    return useMemo(() => {
        const getPaymentIcon = (status: string) => {
            switch (status) {
                case `${PAYMENT_METHOD.CASH}`:
                    return <CashIcon fontSize="small" />;
                case `${PAYMENT_METHOD.CARD}`:
                    return <CardIcon fontSize="small" />;
                default:
                    return null;
            }
        };

        return [
            {
                accessorKey: 'endUserFullName',
                header: t('TABLE_HEADERS.END_USER'),
                filterVariant: 'text',
                enableColumnActions: false,
                enableColumnFilter: false,
                enableSorting: false,
                size: 150,
            },
            {
                accessorKey: 'totalPrice',
                header: t('TABLE_HEADERS.TOTAL_PRICE'),
                enableColumnActions: false,
                enableColumnFilter: false,
                size: 150,
            },
            {
                accessorKey: 'orderStatus',
                header: t('TABLE_HEADERS.ORDER_STATUS'),
                filterVariant: 'select',
                filterSelectOptions: filterSelectOptions,
                enableColumnActions: false,
                enableSorting: false,
                size: 150,
                Cell: ({ cell }) => (
                    <ChipCell
                        value={cell.getValue<string>()}
                        statusColorMap={ORDER_STATUS_COLOR}
                        translationKey="STATUS"
                    />
                ),
            },
            {
                accessorKey: 'deliveryMethod',
                header: t('TABLE_HEADERS.DELIVERY_METHOD'),
                enableColumnActions: false,
                enableSorting: false,
                enableColumnFilter: false,
                size: 150,
            },
            {
                accessorKey: 'createdAt',
                header: t('TABLE_HEADERS.CREATION_DATE'),
                enableColumnActions: false,
                enableColumnFilter: false,
                size: 150,
                Cell: ({ cell }) => formatDateWithTime(cell.getValue<string>(), timeZone),
            },
            {
                accessorKey: 'updatedAt',
                header: t('TABLE_HEADERS._DATE'),
                enableColumnActions: false,
                enableColumnFilter: false,
                size: 150,
                Cell: ({ cell }) => formatDateWithTime(cell.getValue<string>(), timeZone),
            },

            {
                accessorKey: 'paymentMethod',
                header: t('TABLE_HEADERS.PAYMENT_METHOD'),
                enableColumnActions: false,
                enableSorting: false,
                enableColumnFilter: false,
                Cell: ({ cell }) => (
                    <Chip
                        label={
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                {getPaymentIcon(cell.getValue<string>())}
                                {removeUnderscore(t(`TYPES.${cell.getValue<string>()}`))}
                            </Stack>
                        }
                        color={PAYMENT_METHOD_COLOR[cell.getValue<string>()]}
                        variant="outlined"
                        size="small"
                    />
                ),
                size: 150,
            },
        ];
    }, [t, filterSelectOptions]);
};
